/**
 * Centralized React Query key factory.
 * All query keys are defined here to prevent key collisions and enable
 * targeted invalidation.
 */

export const queryKeys = {
  // ── Auth / Profile ──
  profile: (userId: string) => ["profile", userId] as const,
  profileCurrent: () => ["profile", "current"] as const,
  paywallAmounts: () => ["paywall-amounts"] as const,
  profileByUsername: (username: string) => ["profile", "username", username] as const,

  // ── Characters ──
  characters: {
    all: () => ["characters"] as const,
    list: (filters: Record<string, unknown>) =>
      ["characters", "list", filters] as const,
    detail: (id: string, locale?: string) =>
      locale
        ? (["characters", "detail", id, locale] as const)
        : (["characters", "detail", id] as const),
    byUser: (userId: string) => ["characters", "user", userId] as const,
    popular: (locale?: string) =>
      locale
        ? (["characters", "popular", locale] as const)
        : (["characters", "popular"] as const),
    search: (query: string) => ["characters", "search", query] as const,
  },

  // ── Conversations ──
  conversations: {
    all: () => ["conversations"] as const,
    detail: (id: string) => ["conversations", "detail", id] as const,
    byCharacter: (characterId: string) =>
      ["conversations", "character", characterId] as const,
  },

  // ── Messages ──
  messages: {
    byConversation: (conversationId: string) =>
      ["messages", conversationId] as const,
    byConversationWindow: (
      conversationId: string,
      visibleAfter: string | null | undefined
    ) => ["messages", conversationId, { visibleAfter: visibleAfter ?? null }] as const,
  },

  // ── Memories ──
  memories: {
    all: (userId: string) => ["memories", userId] as const,
    forCharacter: (userId: string, characterId: string) =>
      ["memories", userId, characterId] as const,
    settings: (userId: string) => ["memory-settings", userId] as const,
  },

  // ── Known-minor pause (PLE-459) ──
  knownMinor: {
    myResponse: () => ["known-minor", "my-response"] as const,
  },

  // ── Chat Settings (Writing Style) ──
  chatSettings: {
    conversation: (conversationId: string) =>
      ["chat-settings", "conversation", conversationId] as const,
    defaults: (userId: string) => ["chat-settings", "defaults", userId] as const,
  },

  // ── Favorites ──
  favorites: {
    all: () => ["favorites"] as const,
    check: (characterId: string) =>
      ["favorites", "check", characterId] as const,
  },

  // ── Character Likes ──
  characterLikes: {
    check: (characterId: string) =>
      ["characterLikes", "check", characterId] as const,
  },

  // ── Gallery ──
  gallery: {
    feed: (tab: string) => ["gallery", "feed", tab] as const,
    post: (id: string) => ["gallery", "post", id] as const,
    userPosts: (userId: string) => ["gallery", "user", userId] as const,
    likeCheck: (postId: string) =>
      ["gallery", "likeCheck", postId] as const,
    related: (postId: string) => ["gallery", "related", postId] as const,
    userLikes: (userId: string) => ["gallery", "userLikes", userId] as const,
  },

  // ── Generations ──
  generations: {
    all: () => ["generations"] as const,
    detail: (id: string) => ["generations", "detail", id] as const,
    heroPreview: () => ["generations", "hero-preview", "platform-native-v3"] as const,
  },

  // ── Generate Studio ──
  studio: {
    characters: (userId: string) => ["studio", "characters", userId] as const,
  },

  // ── Coins ──
  coins: {
    balance: (userId: string) => ["coins", "balance", userId] as const,
    transactions: (userId: string) =>
      ["coins", "transactions", userId] as const,
    packages: () => ["coins", "packages"] as const,
    subscription: (userId: string) =>
      ["coins", "subscription", userId] as const,
  },

  // ── AI Registry ──
  aiRegistry: {
    assets: (filters?: { type?: string; category?: string }) =>
      ["ai-registry", "assets", filters ?? {}] as const,
    asset: (slug: string) => ["ai-registry", "asset", slug] as const,
    pipelines: (type?: string) =>
      ["ai-registry", "pipelines", type ?? "all"] as const,
    pipeline: (slug: string) => ["ai-registry", "pipeline", slug] as const,
    voices: () => ["ai-registry", "voices"] as const,
  },
} as const
